import React, { useCallback, useEffect, useState } from 'react'
import { InputLabel } from '@material-ui/core'

import useStyles from './styles'
import { useParams, navigate } from '@reach/router'

import { BasicInput, Checkbox, MaskBasicInput, Select } from '_/components/inputs'
import { useDispatch, useSelector } from 'react-redux'
import { getReferaUserDetails } from '_/modules/user/actions'
import { useDialog } from '_/hooks/use-dialog'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  applyCPFMask,
  EMAIL_PROVIDER_OPTIONS,
  ENCRYPTION_METHOD_OPTIONS,
  SELECT_MENU_PROPS,
} from './constants'
import { ROUTES } from '_/utils/constants'
import { AddGroupPermissions } from './add-group-permission'
import { getPermissionsGroupSelector } from '_/modules/profile/selectors'
import { getProfilePermissionGroup } from '_/modules/profile/actions'
import { useToast } from '_/hooks/use-toast'

export function Form({ setIsLoading, permissionsGroup, setPermissionsGroup }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { userId } = useParams()
  const { showDialog, closeDialog } = useDialog()
  const { setValue } = useFormContext()
  const groupsPermission = useSelector(getPermissionsGroupSelector)
  const { showToast } = useToast()
  const sendEmail = useWatch({ name: 'sendEmail', defaultValue: false })
  const [emailConfigDefaults, setEmailConfigDefaults] = useState({
    provider: 'gmail',
    smtpAddress: 'smtp.gmail.com',
    smtpPort: '587',
    encryptionMethod: 'tls',
    username: '',
    password: '',
  })

  const getPermissionsGroup = useCallback(() => {
    if (!groupsPermission?.results || groupsPermission?.results?.length === 0) {
      setIsLoading(true)

      return dispatch(
        getProfilePermissionGroup({
          profile: 'refera',
          pageSize: 1000,
        })
      )
        .catch(() => {
          showToast({
            type: 'error',
            message: 'Ocorreu um erro ao buscar os registros dos grupos de permissões.',
          })
          return setIsLoading(false)
        })
        .finally(() => setIsLoading(false))
    }

    return null
  }, [dispatch])

  const getUserRefera = useCallback(() => {
    setIsLoading(true)
    dispatch(getReferaUserDetails(userId))
      .then(data => {
        const userEmailConfig = data.userEmailConfig || {}
        const resolvedEmailConfigDefaults = {
          provider: userEmailConfig.provider || 'gmail',
          smtpAddress: userEmailConfig.smtpAddress || 'smtp.gmail.com',
          smtpPort: userEmailConfig.smtpPort || '587',
          encryptionMethod: userEmailConfig.encryptionMethod || 'tls',
          username: userEmailConfig.username || '',
          password: userEmailConfig.password || '',
        }

        setValue('name', data.name)
        setValue('cpf', applyCPFMask(data.cpfOrCnpj))
        setValue('email', data.email)
        setValue('admin', data.admin)
        setValue('sendEmail', data.sendEmail)
        setValue('digisacUserId', data.digisacUserId)
        setValue('emailProvider', resolvedEmailConfigDefaults.provider)
        setValue('smtpAddress', resolvedEmailConfigDefaults.smtpAddress)
        setValue('smtpPort', resolvedEmailConfigDefaults.smtpPort)
        setValue('encryptionMethod', resolvedEmailConfigDefaults.encryptionMethod)
        setValue('emailConfigUsername', userEmailConfig.username || '')
        setValue('emailConfigPassword', userEmailConfig.password || '')
        setEmailConfigDefaults(resolvedEmailConfigDefaults)
        setPermissionsGroup(data.permissionsGroup)
      })
      .catch(() => {
        showDialog({
          labelApprove: 'Ok',
          subject: 'Ocorreu um erro ao buscar dados do usuário. Tente novamente mais tarde.',
          type: 'error',
          onApprove: () => {
            closeDialog()
            navigate(ROUTES.REFERA_USERS)
          },
        })
      })
      .finally(() => setIsLoading(false))
  }, [userId, dispatch])

  useEffect(() => {
    getPermissionsGroup()
    if (userId) {
      getUserRefera()
    }
  }, [userId])

  return (
    <form className={styles.form}>
      <div className={styles.main}>
        <div className={styles.subcontainer}>
          <InputLabel className={styles.groupLabel}>Dados principais</InputLabel>
          <div className={styles.wrapper}>
            <BasicInput label="Nome" name="name" autocomplete="name" required />
            <MaskBasicInput
              label="CPF"
              name="cpf"
              mask="999.999.999-99"
              required
              autocomplete="cpf"
              labelClasses={styles.label}
              containerClasses={styles.maskInput}
            />
            <BasicInput
              label="E-mail"
              name="email"
              required
              autocomplete="email"
              disabled={userId}
            />
            <BasicInput label="ID na Plataforma Whatsapp" name="digisacUserId" />
            <div className={styles.checkboxWrapper}>
              <div className={styles.checkboxRow}>
                <Checkbox label="Admin" name="admin" containerClasses={styles.inlineCheckbox} />
                <Checkbox
                  label="Provedor de email"
                  name="sendEmail"
                  defaultValue={false}
                  containerClasses={styles.inlineCheckbox}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.subcontainer}>
          <InputLabel className={styles.groupLabel}>Grupo de permissões</InputLabel>
          <div className={styles.wrapperFull}>
            <AddGroupPermissions
              permissionsGroup={permissionsGroup}
              setPermissionsGroup={setPermissionsGroup}
            />
          </div>
        </div>

        {sendEmail && (
          <div className={styles.subcontainer}>
            <InputLabel className={styles.groupLabel}>Provedor de email</InputLabel>
            <div className={styles.wrapper}>
              <Select
                label="Provedor de Serviço de Email"
                name="emailProvider"
                defaultValue={emailConfigDefaults.provider}
                required={sendEmail}
                options={EMAIL_PROVIDER_OPTIONS}
                getLabel={item => item.label}
                getValue={item => item.value}
                getKey={item => item.value}
                menuProps={SELECT_MENU_PROPS}
              />
              <BasicInput
                label="Endereço SMTP"
                name="smtpAddress"
                defaultValue={emailConfigDefaults.smtpAddress}
                required={sendEmail}
              />
              <BasicInput
                label="Porta SMTP"
                name="smtpPort"
                defaultValue={emailConfigDefaults.smtpPort}
                required={sendEmail}
              />

              <Select
                label="Método de Criptografia"
                name="encryptionMethod"
                defaultValue={emailConfigDefaults.encryptionMethod}
                required={sendEmail}
                options={ENCRYPTION_METHOD_OPTIONS}
                getLabel={item => item.label}
                getValue={item => item.value}
                getKey={item => item.value}
                menuProps={SELECT_MENU_PROPS}
              />
              <BasicInput
                label="Nome de Usuário (Login)"
                name="emailConfigUsername"
                defaultValue={emailConfigDefaults.username}
                required={sendEmail}
              />
              <BasicInput
                label="Senha/API Key"
                name="emailConfigPassword"
                defaultValue={emailConfigDefaults.password}
                type="password"
                required={sendEmail}
              />
            </div>
          </div>
        )}
      </div>
    </form>
  )
}
